import { DOMAIN_CDNGCS, DOMAIN_GCS_BUYMED, DOMAIN_GGCLOUD, URL_CDNGCS, URL_GCS_BUYMED, URL_GGCLOUD } from 'sysconfig';
import { ImageSize } from './ImageFallback';

export const IMAGE_SIZES = {
  thumbnail: {
    width: 150,
    quality: 80,
  },
  medium: {
    width: 300,
    quality: 80,
  },
  medium_large: {
    width: 768,
    quality: 80,
  },
  large: {
    width: 1024,
    quality: 80,
  },
  origin: {
    quality: 100,
  },
};

const replaceGGCLOUDToCDN = (url: string) => {
  if (!url.startsWith(URL_GGCLOUD)) return url;

  const updatedUrl = url.replace(DOMAIN_GGCLOUD, DOMAIN_CDNGCS);

  return updatedUrl;
};

export const getLinkProxyFile = (url = '') => replaceGGCLOUDToCDN(encodeURI(url.replace(/\s/g, '%20')));

export const getLinkCDNVideo = (url = '') => replaceGGCLOUDToCDN(url);

export const getLinkCDNAudio = (url = '') => replaceGGCLOUDToCDN(url);

// --- Image URL Replacement Logic ---

export const shouldReplaceImageUrl = (url: string, domains: string[]): boolean => {
  if (!url) return false;

  return domains?.some((domain) => url?.startsWith(domain));
};

export const convertImageUrl = (url: string, domain: string): string => {
  try {
    const urlObj = new URL(url);
    urlObj.hostname = new URL(domain).hostname; // Replace the hostname with the alternative domain
    return urlObj.toString();
  } catch (error) {
    return url;
  }
};

const getVariantImageGCS = (url: string, size: ImageSize) => {
  // Check SVG
  if (url.endsWith('.svg')) return url;

  const width = IMAGE_SIZES[size]?.width;
  const quality = IMAGE_SIZES[size]?.quality;

  const urlObj = new URL(url);
  const params = new URLSearchParams(urlObj.search);
  if (width) {
    params.set('width', width.toString());
  }
  if (quality) {
    params.set('quality', quality.toString());
  }

  urlObj.search = params.toString();
  return urlObj.toString();
};

const getVariantImageCDNGCS = (url: string, size: ImageSize) => {
  // Check SVG
  if (url.endsWith('.svg')) return url;

  const urlObj = new URL(url);
  const params = new URLSearchParams(urlObj.search);

  switch (size) {
    case 'thumbnail':
      params.set('size', '150');
      break;
    case 'medium':
      params.set('size', '300');
      break;
    case 'medium_large':
      params.set('size', '330');
      break;
    case 'large':
      params.set('size', '360');
      break;
    case 'origin':
      break;
    default:
      params.set('size', '360');
  }

  urlObj.search = params.toString();
  return urlObj.toString();
};

/*
 * Get the link to the CDN image
 * @param url - The URL of the image
 * @param size - The size of the image (thumbnail, medium, medium_large, large, origin)
 * @returns The CDN link for the image
 */
export const getLinkCDNImage = (url = '', size: ImageSize = 'origin') => {
  try {
    if (!shouldReplaceImageUrl(url, [URL_GGCLOUD, URL_CDNGCS])) return url;

    let updatedUrl = url;

    if (url.startsWith(URL_GGCLOUD)) {
      updatedUrl = url.replace(DOMAIN_GGCLOUD, DOMAIN_GCS_BUYMED);
    }

    if (url.startsWith(URL_CDNGCS)) {
      updatedUrl = url.replace(DOMAIN_CDNGCS, DOMAIN_GCS_BUYMED);
    }

    return getImageWithSizeAndQuality(updatedUrl, size);
  } catch (error) {
    console.error('Error in getLinkCDNImage:', error);
    return url;
  }
};

export const getImageWithSizeAndQuality = (url: string, size: ImageSize): string => {
  if (!url) return url;

  if (url.startsWith(URL_GCS_BUYMED)) return getVariantImageGCS(url, size);

  if (url.startsWith(URL_CDNGCS)) return getVariantImageCDNGCS(url, size);

  return url;
};

// ----- Image URL Replacement Logic End ---

export const getLinkCDNImageList = (urls = [], size: ImageSize) => urls?.map((url) => getLinkCDNImage(url, size)) || [];

export const blobToBase64 = (blob: Blob): Promise<string | ArrayBuffer | null> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });

export const escapeBracketsInFileName = (url = '') => {
  let fileName = url.split('/').pop() as string;

  // Check if the file name contains brackets
  if (/\(|\)/.test(fileName)) {
    fileName = fileName.replace(/\(/g, '\\(').replace(/\)/g, '\\)');

    return url.replace(/\/([^\/]+)$/, '/' + fileName);
  }

  return url;
};

